import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors } from '../../styles/GlobalStyles';
import { memberService } from '../../services/memberService';
import { authService } from '../../services/authService';
import { Button } from '../../components/Button';
import { Input, Label, FormGroup } from '../../components/Input';
import { Loader } from '../../components/Loader';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const Container = styled.div``;

const Title = styled.h1`
  color: ${colors.primary};
  margin-bottom: 30px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 30px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 2px 8px ${colors.shadow};
`;

const ProfilePhotoSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ProfilePhoto = styled.img`
  width: 200px;
  height: 200px;
  border-radius: 50%;
  object-fit: cover;
  margin-bottom: 20px;
  border: 4px solid ${colors.primary};
`;

const PhotoPlaceholder = styled.div`
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background-color: ${colors.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.primary};
  font-weight: bold;
  font-size: 72px;
  margin-bottom: 20px;
  border: 4px solid ${colors.primary};
`;

const HiddenInput = styled.input`
  display: none;
`;

const InfoSection = styled.div``;

const InfoItem = styled.div`
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid ${colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.div`
  font-size: 14px;
  color: ${colors.textLight};
  margin-bottom: 5px;
`;

const InfoValue = styled.div`
  font-size: 16px;
  color: ${colors.text};
  font-weight: 500;
`;

const SectionTitle = styled.h2`
  color: ${colors.primary};
  font-size: 20px;
  margin-bottom: 20px;
`;

const Alert = styled.div`
  background-color: #d1ecf1;
  color: #0c5460;
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 14px;
`;

export const MemberProfile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [memberData, setMemberData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [phone, setPhone] = useState('');
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (user?.id) {
      fetchMemberData();
    }
  }, [user]);

  const fetchMemberData = async () => {
    try {
      setLoading(true);
      const data = await memberService.getMember(user.id);
      setMemberData(data);
      setPhone(data.phone || '');
    } catch (error) {
      toast.error('Profil yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 2MB)
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > maxSize) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      toast.error(`Dosya boyutu çok büyük (${fileSizeMB} MB). Maksimum 2 MB olabilir.`, {
        autoClose: 5000
      });
      e.target.value = ''; // Reset input
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen bir resim dosyası seçin (JPG, PNG, GIF, vb.)');
      e.target.value = ''; // Reset input
      return;
    }

    try {
      setUploading(true);
      const result = await memberService.uploadProfilePhoto(user.id, file);
      toast.success('Profil fotoğrafı başarıyla yüklendi!');
      // Update member data
      setMemberData({...memberData, profilePhoto: result.photoUrl});
      e.target.value = ''; // Reset input
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fotoğraf yüklenemedi');
      e.target.value = ''; // Reset input
    } finally {
      setUploading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    try {
      setUpdating(true);
      await memberService.updateProfile(user.id, { phone });
      toast.success('Profil güncellendi');
      fetchMemberData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Profil güncellenemedi');
    } finally {
      setUpdating(false);
    }
  };

  const handlePasswordReset = async () => {
    try {
      await authService.forgotPassword(memberData.email);
      toast.success('Şifre sıfırlama linki email adresinize gönderildi');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Şifre sıfırlama başarısız');
    }
  };

  if (loading) {
    return <Loader fullScreen />;
  }

  return (
    <Container>
      <Title>Profilim</Title>

      <Grid>
        <Card>
          <ProfilePhotoSection>
            {memberData?.profilePhoto ? (
              <ProfilePhoto src={memberData.profilePhoto} alt="Profil Fotoğrafı" />
            ) : (
              <PhotoPlaceholder>
                {memberData?.firstName?.[0]}{memberData?.lastName?.[0]}
              </PhotoPlaceholder>
            )}
            
            <HiddenInput
              type="file"
              id="photo-upload"
              accept="image/*"
              onChange={handlePhotoChange}
            />
            
            <Button 
              as="label" 
              htmlFor="photo-upload"
              disabled={uploading}
              fullWidth
            >
              {uploading ? 'Yükleniyor...' : 'Fotoğraf Değiştir'}
            </Button>
            
            <p style={{
              fontSize: '12px',
              color: '#666',
              marginTop: '10px',
              textAlign: 'center'
            }}>
              📷 JPG, PNG, GIF<br />
              Maksimum boyut: 2 MB
            </p>
          </ProfilePhotoSection>
        </Card>

        <div>
          <Card style={{ marginBottom: '20px' }}>
            <SectionTitle>Kişisel Bilgiler</SectionTitle>
            
            <Alert>
              ℹ️ Ad, soyad, email ve şifre bilgilerinizi değiştirmek için lütfen yönetici ile iletişime geçin.
            </Alert>

            <InfoItem>
              <InfoLabel>Ad Soyad</InfoLabel>
              <InfoValue>{memberData?.firstName} {memberData?.lastName}</InfoValue>
            </InfoItem>

            <InfoItem>
              <InfoLabel>Email</InfoLabel>
              <InfoValue>{memberData?.email}</InfoValue>
            </InfoItem>

            <InfoItem>
              <InfoLabel>Üyelik Başlangıç</InfoLabel>
              <InfoValue>{memberData?.membershipStart}</InfoValue>
            </InfoItem>

            <InfoItem>
              <InfoLabel>Üyelik Bitiş</InfoLabel>
              <InfoValue>{memberData?.membershipEnd}</InfoValue>
            </InfoItem>

            <InfoItem>
              <InfoLabel>Kalan Üyelik Süresi</InfoLabel>
              <InfoValue>{memberData?.remainingDays} Gün</InfoValue>
            </InfoItem>

            <InfoItem>
              <InfoLabel>Rezervasyon Hakkı</InfoLabel>
              <InfoValue>
                {memberData?.remainingReservations} / {memberData?.reservationLimit}
              </InfoValue>
            </InfoItem>
          </Card>

          <Card style={{ marginBottom: '20px' }}>
            <SectionTitle>Düzenlenebilir Bilgiler</SectionTitle>
            
            <form onSubmit={handleUpdateProfile}>
              <FormGroup>
                <Label>Telefon</Label>
                <Input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="05XX XXX XX XX"
                />
              </FormGroup>

              <Button type="submit" disabled={updating}>
                {updating ? 'Güncelleniyor...' : 'Bilgileri Güncelle'}
              </Button>
            </form>
          </Card>

          <Card>
            <SectionTitle>Şifre Yenileme</SectionTitle>
            
            <Alert>
              ℹ️ Şifre yenileme linki kayıtlı email adresinize gönderilecektir.
            </Alert>

            <Button onClick={handlePasswordReset}>
              Şifre Yenileme Linki Gönder
            </Button>
          </Card>
        </div>
      </Grid>
    </Container>
  );
};

